import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { trackEvent } from '../utils/tracking';

// El método H·E·R — dos capas
// CAPA 1 (la fase): descripción + Objetivo / Alimentación / Qué observar.
// CAPA 2 ("Tu recorrido"): el mapa de los 7 días en tres bloques —
//   Arranque (o Nueva fase) → Práctica → Checkpoint.
// El detalle de cada día vive dentro de la experiencia interactiva del Protocolo;
// acá solo se muestra el mapa. Un solo bloque abierto a la vez.
//
// SIN VISTAS PREVIAS: los recursos de "Apoyo H.E.R." (Recetario, Tracker, H.E.R. Movement, Noche en Calma) son
// etiquetas informativas, no botones. La demostración del producto por dentro la cubre el video "Así se ve H.E.R.
// por dentro" (components/HerDemoVideo.tsx). Los archivos components/method/MethodPreviews.tsx y previewData.ts se
// conservan sin usar por si se quieren reactivar.
//
// REGLA DE LENGUAJE (auditoría de claims): el texto puede hablar con calidez y de lo que la clienta busca
// (sentirse mejor, recuperar una rutina), pero no afirma mecanismos fisiológicos (cortisol, inflamación,
// tiroides, suprarrenales, "modular" señales hormonales) ni garantiza resultados. Pendiente de decisión aparte: lácteos.
// Las tres fases comparten registro, cada una con su identidad: H acompaña esta etapa, E construye una rutina,
// R consolida lo aprendido. Hilo conductor: hábitos que puedas sostener después del día 21.
//
// SUBORDINADO AL PROTOCOLO REAL: los textos de cada fase salen del Protocolo interactivo (HER_Protocolo_final).
// Si el Protocolo cambia, se actualiza acá; no se agregan reglas que el Protocolo no tenga.
// El Tracker del Protocolo registra sueño, cansancio y ánimo (escala 1 a 5): "Qué observar" no promete
// que el Tracker mida otras cosas. El aviso médico de la Fase E (condición médica, medicación, lactancia)
// acompaña al cambio de horarios y no se quita. La línea de Alimentación de la Fase R repite lo que dice el
// Protocolo (mantener la base y sumar variedad de a poco); no se agregan recomendaciones nutricionales.
//
// CONSISTENCIA CON H.E.R. MOVEMENT: los días 14 y 21 son "Recuperación · libre".
// Los minutos y el ritmo de fuerza (días 8/10/12 y 15/17/19) salen del Protocolo.

// Recursos válidos: 'protocolo' | 'recetario' | 'movement' | 'noche' | 'tracker' | 'organizacion'
// (los mismos del kit del Hero).
export type ResourceId = 'protocolo' | 'recetario' | 'movement' | 'noche' | 'tracker' | 'organizacion';
// Se conserva por compatibilidad con importaciones previas; ya no se usa en esta sección.
export type DayContent = {
  title?: string;
  focus?: string;
  action?: string;
  support?: ResourceId[];
};
const RESOURCE_LABEL: Record<ResourceId, string> = {
  protocolo: 'Protocolo H.E.R.',
  recetario: 'Recetario H.E.R.',
  movement: 'H.E.R. Movement',
  noche: 'Noche en Calma',
  tracker: 'Tracker',
  organizacion: 'Organización'
};

// ---------------------------------------------------------------------------
// TU RECORRIDO — tres bloques por fase
// ---------------------------------------------------------------------------
type JourneyStep = {
  range: string; // "Día 1", "Días 2–6", "Día 7"
  title: string; // Arranque / Nueva fase / Práctica / Checkpoint
  today?: string; // campo "Hoy"
  text?: string; // texto breve (bloque Práctica)
  movement?: string; // campo "Movimiento" (se acompaña de la etiqueta H.E.R. Movement)
  note?: string; // línea gris breve (ritmo de fuerza)
  support: ResourceId[]; // campo "Apoyo H.E.R."
  final?: boolean; // último bloque del recorrido (nodo relleno)
};
const PRACTICE_TEXT = 'Seguís aplicando la base de la fase. Cada día del protocolo te indica qué hacer ese día.';
const CHECKPOINT_TODAY = 'Hacé tu checkpoint en el Tracker y cerrá la fase.';
const JOURNEY: Record<string, JourneyStep[]> = {
  H: [{
    range: 'Día 1',
    title: 'Arranque',
    today: 'Registrá tu punto de partida en el Tracker y empezá la rutina del día.',
    movement: 'Movilidad y activación · 15 min',
    support: ['tracker', 'recetario', 'noche']
  }, {
    range: 'Días 2–6',
    title: 'Práctica',
    text: PRACTICE_TEXT,
    support: ['movement', 'recetario', 'noche']
  }, {
    range: 'Día 7',
    title: 'Checkpoint',
    today: CHECKPOINT_TODAY,
    movement: 'Movilidad y activación · 15 min',
    support: ['tracker', 'recetario', 'noche'],
    final: true
  }],
  E: [{
    range: 'Día 8',
    title: 'Nueva fase',
    today: 'Hoy cambia tu rutina: horarios de comida más parecidos, hábitos de mañana y fuerza suave.',
    movement: 'Fuerza suave · 20 min',
    support: ['recetario', 'noche']
  }, {
    range: 'Días 9–13',
    title: 'Práctica',
    text: PRACTICE_TEXT,
    note: 'Fuerza suave los días 10 y 12; los otros días, movimiento suave.',
    support: ['movement', 'recetario', 'noche']
  }, {
    range: 'Día 14',
    title: 'Checkpoint',
    today: CHECKPOINT_TODAY,
    movement: 'Recuperación · libre',
    support: ['tracker', 'recetario', 'noche'],
    final: true
  }],
  R: [{
    range: 'Día 15',
    title: 'Nueva fase',
    today: 'Hoy cambia tu rutina: mantenés la base de las fases anteriores y sumás variedad de a poco.',
    movement: 'Fuerza funcional · 25 min',
    support: ['recetario', 'noche']
  }, {
    range: 'Días 16–20',
    title: 'Práctica',
    text: PRACTICE_TEXT,
    note: 'Fuerza funcional los días 17 y 19; los otros días, movimiento suave.',
    support: ['movement', 'recetario', 'noche']
  }, {
    range: 'Día 21',
    title: 'Checkpoint',
    today: 'Hacé tu evaluación final en el Tracker y cerrá el recorrido.',
    movement: 'Recuperación · libre',
    support: ['tracker', 'recetario', 'noche'],
    final: true
  }]
};
const phases = [{
  id: 'H',
  days: 'DÍAS 1—7',
  name: 'Hormonal',
  tagline: 'Volvé a sentirte más en equilibrio.',
  description: 'Volvé a sentirte más en equilibrio.\n\nEmpezá por pequeños cambios en tu alimentación, movimiento y descanso para acompañar esta etapa y recuperar una rutina que te haga sentir mejor.',
  detail: [{
    label: 'Objetivo',
    text: 'Construir hábitos que acompañen tu bienestar y favorezcan un descanso más regular.'
  }, {
    label: 'Alimentación',
    text: 'Proteínas, grasas saludables y vegetales como base de tus comidas, y menos azúcar refinada, alcohol y ultraprocesados.'
  }, {
    label: 'Qué observar',
    text: 'Hinchazón, sueño, cansancio y ánimo. Registrá cómo te sentís en el Tracker.'
  }]
}, {
  id: 'E',
  days: 'DÍAS 8—14',
  name: 'Energía',
  tagline: 'Construí una rutina que acompañe tu energía.',
  description: 'Construí una rutina que acompañe tu energía.\n\nEn esta etapa sumás horarios de comida más regulares, una rutina de mañana y ejercicios de fuerza suave para construir hábitos que puedas sostener.',
  detail: [{
    label: 'Objetivo',
    text: 'Dar más estructura a tu día con horarios de comida regulares y movimiento de fuerza suave.'
  }, {
    label: 'Alimentación',
    text: 'Elegí horarios parecidos para tus comidas. Si tenés una condición médica, tomás medicación o estás amamantando, consultá antes de cambiar tus horarios.'
  }, {
    label: 'Qué observar',
    text: 'Registrá tu sueño, cansancio y ánimo para reconocer tus propios patrones.'
  }]
}, {
  id: 'R',
  days: 'DÍAS 15—21',
  name: 'Restaurativa',
  tagline: 'Convertí tus nuevos hábitos en una forma de cuidarte.',
  description: 'Convertí tus nuevos hábitos en una forma de cuidarte.\n\nEn esta última etapa integrás lo aprendido durante los 21 días y construís una rutina que puedas mantener en tu vida cotidiana. No es el final: después tenés una semana de continuación para seguir a tu ritmo.',
  detail: [{
    label: 'Objetivo',
    text: 'Consolidar los hábitos de alimentación, movimiento y descanso que mejor se adapten a tu rutina.'
  }, {
    label: 'Alimentación',
    text: 'Mantener la base de proteína y vegetales y sumar de a poco más variedad de alimentos, observando qué combinaciones te resultan prácticas.'
  }, {
    label: 'Qué observar',
    text: 'Compará tus registros del Día 1 y del Día 21 en sueño, cansancio y ánimo, y cómo te sentís al cerrar el recorrido.'
  }]
}];
function Chevron({
  open
}: {
  open: boolean;
}) {
  return <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" className="flex-shrink-0 transition-transform duration-300" style={{
    transform: open ? 'rotate(180deg)' : 'rotate(0deg)'
  }} data-id="element-623" data-mp="y6z4h3x3h4fz">
      <path d="M2.5 4.5 6 8l3.5-3.5" stroke="#C8A870" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" data-id="element-624" data-mp="ognyzcxrin6z" />
    </svg>;
}
// Etiquetas informativas (no son botones).
function ResourceChips({
  ids
}: {
  ids: ResourceId[];
}) {
  return <div className="flex flex-wrap gap-2" data-id="element-625" data-mp="kgnaezgy8vm7">
      {ids.map(id => <span key={id} className="rounded-full border border-[#C8A870]/35 bg-[#C8A870]/[0.05] px-3 py-1 text-[12px] text-[#F0E8D8]/85" style={{
      fontFamily: 'sans-serif'
    }} data-id="element-626" data-mp="kh5cicqam421">
          {RESOURCE_LABEL[id]}
        </span>)}
    </div>;
}
function FieldLabel({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="mb-1.5 text-[10.5px] uppercase tracking-[0.16em] text-[#C8A870]" style={{
    fontFamily: 'sans-serif'
  }} data-id="element-627" data-mp="3n44rndt2tsiu">
      {children}
    </p>;
}
function JourneyStepItem({
  step,
  last
}: {
  step: JourneyStep;
  last: boolean;
}) {
  return <li className="relative pb-6 pl-[26px] last:pb-1" data-id="element-628" data-mp="3s0vh2rqru0gx">
      {/* Línea del recorrido y nodo */}
      {!last && <span aria-hidden="true" className="absolute left-[4px] top-[10px] -bottom-1 w-px" style={{
      background: 'rgba(200,168,112,0.28)'
    }} data-id="element-629" data-mp="9uguk4efsi6x" />}
      <span aria-hidden="true" className="absolute left-0 top-[6px] h-[9px] w-[9px] rounded-full border border-[#C8A870]" style={{
      background: step.final ? '#C8A870' : '#0a0807'
    }} data-id="element-630" data-mp="5s3zqb18a4ox" />

      <p className="text-[10.5px] uppercase tracking-[0.16em] text-[#C8A870] font-semibold" style={{
      fontFamily: 'sans-serif'
    }} data-id="element-631" data-mp="eobk3ogq3rdx">
        {step.range}
      </p>
      <p className="mt-0.5 mb-2 font-display leading-tight text-[#E8D5B0]" style={{
      fontSize: '21px'
    }} data-id="element-632" data-mp="olziqphg9j1d">
        {step.title}
      </p>

      <div className="space-y-3" data-id="element-633" data-mp="vh10lwdtreah">
        {step.text && <p className="text-[#F0E8D8]/78" style={{
        fontFamily: 'sans-serif',
        fontSize: '14.5px',
        lineHeight: 1.55
      }} data-id="element-634" data-mp="vg99axr9g2q0">
            {step.text}
          </p>}
        {step.note && <p className="italic text-[#F0E8D8]/60" style={{
        fontFamily: 'sans-serif',
        fontSize: '13.5px',
        lineHeight: 1.5
      }} data-id="element-635" data-mp="mhxjgxy7x8ym">
            {step.note}
          </p>}
        {step.today && <div data-id="element-636" data-mp="1e8232ue5lrys">
            <FieldLabel data-id="element-637" data-mp="1e5fquhgbioa8">Hoy</FieldLabel>
            <p className="text-[#F0E8D8]/75" style={{
          fontFamily: 'sans-serif',
          fontSize: '14.5px',
          lineHeight: 1.6
        }} data-id="element-638" data-mp="1a5fd6z11mh48">
              {step.today}
            </p>
          </div>}
        {step.movement && <div data-id="element-639" data-mp="3uxqzzcsjukfo">
            <FieldLabel data-id="element-640" data-mp="3uxh63e4dvt4p">Movimiento</FieldLabel>
            <p className="mb-2 text-[#F0E8D8]/75" style={{
          fontFamily: 'sans-serif',
          fontSize: '14.5px',
          lineHeight: 1.6
        }} data-id="element-641" data-mp="3pwjrtcmhvj8h">
              {step.movement}
            </p>
            <ResourceChips ids={['movement']} data-id="element-642" data-mp="2adc6okikaz59" />
          </div>}
        {step.support.length > 0 && <div data-id="element-643" data-mp="2ef76tlorb6hg">
            <FieldLabel data-id="element-644" data-mp="2eenuqllsfcbc">Apoyo H.E.R.</FieldLabel>
            <ResourceChips ids={step.support} data-id="element-645" data-mp="2edvzjbw3rgll" />
          </div>}
      </div>
    </li>;
}
export function ProtocolSection() {
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const rowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const toggle = (i: number) => {
    const next = activeIdx === i ? null : i;
    setActiveIdx(next);
    trackEvent('method_phase_toggle', {
      phase: phases[i].id,
      open: next !== null
    });
    if (next !== null) {
      // Al abrir una fase se cierra la anterior (puede estar arriba y correr el layout):
      // llevamos la fase abierta al inicio de la vista.
      window.setTimeout(() => {
        rowRefs.current[next]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 80);
    }
  };
  return <section className="bg-gradient-section py-20 md:py-28 px-5 md:px-8" style={{
    paddingBottom: "2px",
    paddingTop: "0px"
  }} data-id="element-646" data-mp="2b40scjb34zaj">
      <div className="max-w-2xl mx-auto" data-id="element-647" data-mp="2k34czrpwhmr5">

        {/* Intro */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="relative text-center mb-14 md:mb-16" data-id="element-648" data-mp="2g1af1r73awuo">
          <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[280px] w-[280px] md:h-[380px] md:w-[380px] rounded-full blur-3xl" style={{
          background: 'radial-gradient(circle, rgba(200,168,112,0.10), transparent 70%)'
        }} data-id="element-649" data-mp="2p1fl9y0jrp79" />

          <h2 className="relative font-display font-semibold text-[#F0E8D8] leading-[1.1]" style={{
          fontSize: "33px"
        }} data-id="element-650" data-mp="3crt5j2cw4jra">
            El método <span className="italic text-gradient-gold" data-id="element-651" data-mp="3rdqmatyn8tot">H·E·R</span>
          </h2>

          <div aria-hidden="true" className="relative mx-auto mt-5 mb-5 flex w-full max-w-[180px] items-center justify-center" data-id="element-652" data-mp="7p4m2k6b1u7">
            <div className="h-px w-full" style={{
            background: 'linear-gradient(to right, transparent, rgba(200,168,112,0.4), transparent)'
          }} data-id="element-653" data-mp="3icim26ckhis3" />
            <span className="absolute h-[3px] w-[3px] rounded-full" style={{
            background: '#E8D5B0',
            boxShadow: '0 0 6px 1px rgba(232,213,176,0.6)'
          }} data-id="element-654" data-mp="38nqvo2n5n6ja" />
          </div>

          <p className="relative uppercase tracking-[0.28em] font-body text-[#F0E8D8]/70 text-[12px]" style={{
          fontFamily: 'sans-serif',
          color: "#C9A96E",
          fontSize: "13px"
        }} data-id="element-655" data-mp="2ypup7o55wzri">
            21 días · 3 fases · cada paso prepara el siguiente
          </p>
        </motion.div>

        {/* Journey bar — H ─── E ─── R */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.2
      }} className="flex items-center mb-14" data-id="element-656" data-mp="2ung3mwa3zlwj">
          {phases.map((phase, i) => <React.Fragment key={phase.id} data-id="element-657" data-mp="2cwf7qdzv7gj7" data-mpi={i}>
              <button onClick={() => toggle(i)} aria-label={`Fase ${phase.id}: ${phase.name}`} className="flex flex-col items-center gap-1.5 group transition-all duration-400 flex-shrink-0" data-id="element-658" data-mp="2cvdv4537alzd">
                <div className={`w-9 h-9 rounded-full border flex items-center justify-center text-[12px] font-bold transition-all duration-400
                  ${activeIdx === i ? 'border-[#C8A870] text-[#0a0807] bg-[#C8A870]' : 'border-[rgba(200,168,112,0.35)] text-[#C8A870] bg-transparent group-hover:border-[#C8A870]/60'}`} style={{
              fontFamily: 'sans-serif'
            }} data-id="element-659" data-mp="23y3hwe6tomrt">
                  {phase.id}
                </div>
                <span className={`text-[10px] tracking-[0.12em] transition-colors duration-300
                    ${activeIdx === i ? 'text-[#C8A870]' : 'text-[rgba(200,168,112,0.45)]'}`} style={{
              fontFamily: 'sans-serif'
            }} data-id="element-660" data-mp="3kaiou3z051jb">
                  {phase.name}
                </span>
              </button>

              {i < phases.length - 1 && <div className={`flex-1 h-px mx-2 transition-all duration-500
                    ${activeIdx !== null && activeIdx > i ? 'bg-[#C8A870]' : 'bg-[rgba(200,168,112,0.15)]'}`} data-id="element-661" data-mp="1fv6zm62ik1bo" />}
            </React.Fragment>)}
        </motion.div>

        {/* Fases: explicación → abrir → Tu recorrido → cerrar */}
        <div data-id="element-662" data-mp="s5m6wvgk5pp7">
          {phases.map((phase, i) => {
          const isOpen = activeIdx === i;
          return <motion.div key={phase.id} ref={el => {
            rowRefs.current[i] = el;
          }} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: i * 0.1
          }} className={`group relative scroll-mt-4 py-10 md:py-12 transition-all duration-300
                ${i !== 0 ? 'border-t-[0.5px] border-gold/12' : ''}
                ${isOpen ? 'border-l-2 border-[#C8A870] pl-5 md:pl-7 -ml-5 md:-ml-7' : ''}
              `} data-id="element-663" data-mp="w7g1qn42nzhg" data-mpi={i}>
              {/* Giant ghost letter */}
              <span className={`absolute -top-2 left-0 font-display font-medium select-none pointer-events-none transition-all duration-300
                ${isOpen ? 'text-gold/25' : 'text-gold/10'}`} style={{
              fontSize: 'clamp(80px, 18vw, 130px)',
              lineHeight: 1
            }} data-id="element-664" data-mp="hhhuw2bruyv0">
                {phase.id}
              </span>

              {/* Resumen (siempre visible, tappable) */}
              <div role="button" tabIndex={0} aria-expanded={isOpen} onClick={() => toggle(i)} onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggle(i);
              }
            }} className="relative cursor-pointer pl-1 focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" data-id="element-665" data-mp="3uts47oo8zcvh">
                <p className="text-right uppercase tracking-[0.2em] text-[11px] text-[#C8A870] font-semibold" style={{
                fontFamily: 'sans-serif',
                fontSize: "12px"
              }} data-id="element-666" data-mp="1mg51jn7yko1a">
                  {phase.days}
                </p>

                <div className={`h-px bg-gold/70 mt-10 md:mt-12 mb-4 transition-all duration-300
                  ${isOpen ? 'w-14 bg-gold' : 'w-8 group-hover:w-12 group-hover:bg-gold/80'}`} data-id="element-667" data-mp="20f4fo0xuhkj7" />

                <h3 className={`font-display font-medium leading-tight transition-colors duration-300
                  ${isOpen ? 'text-[#C8A870]' : 'text-[#E8D5B0] group-hover:text-gold-light'}`} style={{
                fontSize: "25px"
              }} data-id="element-668" data-mp="1re6l099aqpda">
                  {phase.name}
                </h3>

                <p className="font-body mt-3 max-w-md text-[#F0E8D8]/75 whitespace-pre-line" style={{
                fontSize: "15,5px",
                lineHeight: 1.7
              }} data-id="element-669" data-mp="kxugzrjsmd9s">
                  {phase.description}
                </p>

                <span className="mt-5 inline-flex items-center gap-2 border-b border-[#C8A870]/40 pb-1 uppercase tracking-[0.16em] text-[#C8A870]" style={{
                fontFamily: 'sans-serif',
                fontSize: '12px'
              }} data-id="element-670" data-mp="17l18pugssg7m">
                  {isOpen ? 'Ocultar el recorrido' : 'Ver el recorrido'}
                  <Chevron open={isOpen} data-id="element-671" data-mp="1vdzu1llm7moc" />
                </span>
              </div>

              {/* Detalle: objetivo / alimentación / qué observar + Tu recorrido */}
              <AnimatePresence initial={false} data-id="element-672" data-mp="1lksre8i9ekry">
                {isOpen && <motion.div key="phase-detail" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.4,
                ease: [0.23, 1, 0.32, 1]
              }} className="relative overflow-hidden pl-1" data-id="element-673" data-mp="1gnskjvdl5dzs">
                    <div className="mt-6 space-y-3 border-t border-[rgba(200,168,112,0.12)] pt-5" data-id="element-674" data-mp="2lmpf8yb9b094">
                      {phase.detail.map(row => <div key={row.label} className="flex gap-3" data-id="element-675" data-mp="2qnmyzfbx02cq">
                          <span className="text-[10px] uppercase tracking-[0.12em] text-[#C8A870] mt-0.5 flex-shrink-0" style={{
                      fontFamily: 'sans-serif',
                      minWidth: 80,
                      fontSize: "11px"
                    }} data-id="element-676" data-mp="2vly6vking2sb">
                            {row.label}
                          </span>
                          <span className="text-[#F0E8D8]/70 text-[13px]" style={{
                      fontFamily: 'sans-serif',
                      lineHeight: 1.5,
                      fontSize: "14px"
                    }} data-id="element-677" data-mp="3ealadyl6gk3s">
                            {row.text}
                          </span>
                        </div>)}
                    </div>

                    <div className="mt-6 border-t-[0.5px] border-gold/12 pt-5" data-id="element-678" data-mp="qo3hgdfmrwio">
                      <p className="mb-4 text-[11px] uppercase tracking-[0.2em] text-[#C8A870]" style={{
                    fontFamily: 'sans-serif'
                  }} data-id="element-679" data-mp="vm52v50j47xb">
                        Tu recorrido
                      </p>
                      <ol aria-label={`Recorrido de la fase ${phase.id}`} className="m-0 list-none p-0" data-id="element-680" data-mp="14jp5oie1ica1">
                        {JOURNEY[phase.id].map((step, k, arr) => <JourneyStepItem key={step.range} step={step} last={k === arr.length - 1} data-id="element-681" data-mp="1ej688qxzwa1s" data-mpi={k} />)}
                      </ol>
                    </div>

                    <button type="button" onClick={() => toggle(i)} className="mt-5 flex items-center gap-2 uppercase tracking-[0.16em] text-[#C8A870]/70 transition-colors hover:text-[#C8A870] focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" style={{
                  fontFamily: 'sans-serif',
                  fontSize: '11.5px'
                }} data-id="element-682" data-mp="1jdaj3ladt285">
                      Cerrar fase {phase.id}
                      <Chevron open data-id="element-683" data-mp="3ctwybvfiii9o" />
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>;
        })}
        </div>
      </div>
    </section>;
}